// 카카오맵 기본 마커 생성 함수 (레거시 호환용)
export function createKakaoMarker(map, position) {
  if (!window.kakao || !window.kakao.maps) return null;

  const marker = new window.kakao.maps.Marker({
    position: position,
    map: map,
  });

  return marker;
}

/**
 * 커스텀 원형 핀 생성 함수
 * @param {Object} map - 카카오맵 인스턴스
 * @param {Object} position - 위치 (LatLng 객체)
 * @param {string} color - 핀 색상 (hex 코드)
 * @param {string} areaName - 상권명 (선택사항)
 * @param {Function} onClick - 클릭 이벤트 핸들러 (선택사항)
 * @returns {Object} 커스텀 오버레이 객체
 */
export function createCustomPinMarker(map, position, color, areaName = '', onClick = null) {
  if (!window.kakao || !window.kakao.maps) return null;

  // 원형 핀을 위한 HTML 요소 생성
  const pinElement = document.createElement('div');
  pinElement.className = 'custom-pin';
  
  // 하이라이트 효과를 위한 내부 원
  const innerCircle = document.createElement('div');
  innerCircle.className = 'custom-pin-inner';
  
  // 스타일 설정
  const pinSize = 16; // 작은 크기로 설정
  const shadowBlur = 4;
  const shadowOffset = 2;
  const borderWidth = 2; // 명확한 테두리 두께
  
  // 어두운 색상인지 밝은 색상인지 판단하여 하이라이트 색상 결정
  const rgb = hexToRgb(color);
  const brightness = rgb ? (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000 : 128;
  const highlightColor = brightness > 150 
    ? `rgba(255, 255, 255, 0.5)` // 밝은 색상이면 흰색 하이라이트
    : `rgba(255, 255, 255, 0.7)`; // 어두운 색상이면 더 밝은 흰색 하이라이트
  
  // 테두리 색상: 어두운 회색으로 명확하게 구분
  const borderColor = 'rgba(0, 0, 0, 0.6)';
  
  pinElement.style.cssText = `
    width: ${pinSize}px;
    height: ${pinSize}px;
    border-radius: 50%;
    background: ${color};
    border: ${borderWidth}px solid ${borderColor};
    box-shadow: 
      0 ${shadowOffset}px ${shadowBlur}px rgba(0, 0, 0, 0.4),
      inset 0 -2px 4px rgba(0, 0, 0, 0.2),
      inset 0 1px 2px ${highlightColor};
    cursor: pointer;
    position: relative;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
    z-index: 1;
  `;
  
  innerCircle.style.cssText = `
    position: absolute;
    top: 20%;
    left: 25%;
    width: 35%;
    height: 35%;
    border-radius: 50%;
    background: ${highlightColor};
    pointer-events: none;
  `;
  
  pinElement.appendChild(innerCircle);
  
  // 클릭 이벤트 핸들러 추가
  if (onClick && typeof onClick === 'function') {
    pinElement.addEventListener('click', (e) => {
      e.stopPropagation(); // 이벤트 버블링 방지
      onClick(e);
    });
  }
  
  // 호버 효과
  pinElement.addEventListener('mouseenter', () => {
    pinElement.style.transform = 'scale(1.2)';
    pinElement.style.borderColor = 'rgba(0, 0, 0, 0.8)';
    pinElement.style.boxShadow = `
      0 ${shadowOffset + 1}px ${shadowBlur + 2}px rgba(0, 0, 0, 0.5),
      inset 0 -2px 4px rgba(0, 0, 0, 0.2),
      inset 0 1px 2px ${highlightColor}
    `;
  });
  
  pinElement.addEventListener('mouseleave', () => {
    pinElement.style.transform = 'scale(1)';
    pinElement.style.borderColor = borderColor;
    pinElement.style.boxShadow = `
      0 ${shadowOffset}px ${shadowBlur}px rgba(0, 0, 0, 0.4),
      inset 0 -2px 4px rgba(0, 0, 0, 0.2),
      inset 0 1px 2px ${highlightColor}
    `;
  });
  
  // 커스텀 오버레이 생성
  const customOverlay = new window.kakao.maps.CustomOverlay({
    position: position,
    content: pinElement,
    yAnchor: 0.5,
    xAnchor: 0.5,
    zIndex: 1
  });

  customOverlay.setMap(map);

  // 카카오맵 예제처럼 마커 객체에 직접 핀 요소 참조 저장 (하이라이트용)
  customOverlay.pinElement = pinElement;

  return customOverlay;
}

/**
 * Hex 색상을 RGB로 변환하는 헬퍼 함수
 * @param {string} hex - Hex 색상 코드 (# 포함 또는 미포함)
 * @returns {Object|null} {r, g, b} 객체 또는 null
 */
function hexToRgb(hex) {
  // # 제거
  const cleanHex = hex.replace('#', '');
  
  // 3자리 hex를 6자리로 변환
  const fullHex = cleanHex.length === 3
    ? cleanHex.split('').map(char => char + char).join('')
    : cleanHex;
  
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}
